import React, { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { ProjectGq } from '../../../../graphql/projects/models/ProjectGq';
import { QueuedItemStatus } from '../../../../graphql/projects/models/QueuedItemStatus';
import { projectsApiService } from '../../../../graphql/projects/projectsApiService';
import VoxelAlert from '../../../../components/voxel/Alert/VoxelAlert';
import VoxelDialog from '../../../../components/voxel/Dialogs/VoxelDialog';
import VoxelSelect from '../../../../components/voxel/Forms/VoxelSelect';
import VoxelText from '../../../../components/voxel/Text/VoxelText';
import { GenerateTagsCommandGq } from '../../../../graphql/projects/mutations/tags/useGenerateTagsGq';
import { chain } from 'lodash';
import VoxelDialogContent from '../../../../components/voxel/Dialogs/VoxelDialogContent';
import VoxelDialogActionCancel from '../../../../components/voxel/Dialogs/VoxelDialogActionCancel';
import VoxelDialogActionSubmit from '../../../../components/voxel/Dialogs/VoxelDialogActionSubmit';
import VoxelDialogActions from '../../../../components/voxel/Dialogs/VoxelDialogActions';

export interface GenerateTagGroupDialogProps {
  openDialog: boolean;
  setOpenDialog: Dispatch<SetStateAction<boolean>>;
  project: ProjectGq;
  reload: () => Promise<any>;
  showTagGroup: (tagGroupId: number) => void;
}

const GenerateTagGroupDialog: React.FC<GenerateTagGroupDialogProps> = ({
  openDialog,
  setOpenDialog,
  project,
  reload,
  showTagGroup,
}) => {
  const closeDialog = () => {
    setOpenDialog(false);
  };

  const activeProcess = useMemo(
    () =>
      project.tagGroups.some(
        (tg) => tg.tagGenerationRequest?.status === QueuedItemStatus.PROCESSING
      ),
    [project?.tagGroups]
  );

  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState<GenerateTagsCommandGq>({
    projectId: project.id,
    urlThreshold: 5,
    keywordThreshold: 5,
    ranksToConsider: 10,
  });
  const ranksToConsiderOptions = [10, 20, 30];
  const urlThresholdOptions = [2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 25].filter(
    (urlThreshold) => urlThreshold < formValues.ranksToConsider
  );
  const keywordThresholdOptions = [2, 3, 4, 5, 10, 15, 20, 25, 50, 100];

  const generateTagGroupMutation = projectsApiService.mutations.tags.useGenerateTags();
  const handleGenerateTagGroup = useCallback(() => {
    setLoading(true);
    return generateTagGroupMutation(formValues)
      .then((response) =>
        reload().then(() => {
          showTagGroup(response.tagGroupId);
        })
      )
      .then(closeDialog)
      .finally(() => setLoading(false));
  }, [formValues]);

  const disabled = loading || activeProcess || !project.serpTriggered;

  return (
    <VoxelDialog
      open={openDialog}
      onClose={closeDialog}
      title="Generate tag group"
      subtitle="Clusters keywords based on SERP overlap. 
      Tags will be named after one of the clustered keywords."
    >
      <VoxelDialogContent className="flex flex-col gap-6">
        <VoxelText variant="form-caption" paddingBot>
          Note: Will not be automatically updated if you fetch new keywords or new rank data
        </VoxelText>
        <VoxelSelect
          label="Ranking sites in consideration"
          labelPosition="top"
          options={ranksToConsiderOptions.map((x) => ({
            value: x,
            title: 'Top ' + x.toString(),
          }))}
          setSelectedOption={(opt) =>
            setFormValues((prev) => {
              const next = { ...prev, ranksToConsider: opt.value };
              if (next.urlThreshold >= next.ranksToConsider) {
                next.urlThreshold = chain(urlThresholdOptions)
                  .orderBy((v) => v, 'desc')
                  .find((urlThreshold) => urlThreshold < next.ranksToConsider)
                  .value();
              }
              return next;
            })
          }
          selectedOptionValue={formValues.ranksToConsider}
          disabled={disabled}
        />
        <VoxelSelect
          label="SERP overlap"
          labelPosition="top"
          options={urlThresholdOptions.map((x) => ({
            value: x,
            title: 'At least ' + x.toString(),
          }))}
          setSelectedOption={(opt) =>
            setFormValues((prev) => ({ ...prev, urlThreshold: opt.value }))
          }
          selectedOptionValue={formValues.urlThreshold}
          disabled={disabled}
        />
        <VoxelSelect
          label="Keywords per tag"
          labelPosition="top"
          options={keywordThresholdOptions.map((x) => ({
            value: x,
            title: 'At least ' + x.toString(),
          }))}
          setSelectedOption={(opt) =>
            setFormValues((prev) => ({ ...prev, keywordThreshold: opt.value }))
          }
          selectedOptionValue={formValues.keywordThreshold}
          disabled={disabled}
        />
      </VoxelDialogContent>
      <VoxelDialogActions>
        <VoxelDialogActionCancel onClick={closeDialog} />
        <VoxelDialogActionSubmit
          title={'Proceed'}
          onClick={handleGenerateTagGroup}
          disabled={disabled}
        />
      </VoxelDialogActions>
      {!loading && activeProcess && (
        <VoxelAlert title="Generation already in process" severity="warning" size="sm">
          You can only autogenerate one tag group at a time
        </VoxelAlert>
      )}
      {!project.serpTriggered && (
        <VoxelAlert severity="error" size="sm" iconSize="lg" roundedClass="rounded-t-none">
          Keywords must have rank data to make use of this feature
        </VoxelAlert>
      )}
    </VoxelDialog>
  );
};

export default GenerateTagGroupDialog;
